import { supabase } from '../supabase';
import { dedupeRequest, getCacheKey } from './queryDeduplication';

class NotificationService {
  /**
   * Create a notification for a user
   * @param {string} userId - The user to notify
   * @param {string} type - The type of notification
   * @param {string} relatedId - The ID of the related item (post, comment, etc.)
   * @param {string} relatedUserId - The ID of the user who triggered the notification
   */
  static async createNotification(userId, type, relatedId = null, relatedUserId = null) {
    try {
      // Don't create notification if it's the same user
      if (userId === relatedUserId) {
        console.log(`[NotificationService] Skipping notification - same user: ${userId}`);
        return;
      }

      // Add logging for debugging
      console.log(`[NotificationService] Creating notification:`, {
        userId,
        type,
        relatedId,
        relatedUserId,
        timestamp: new Date().toISOString()
      });

      // Check if notification already exists (idempotency check)
      if (relatedId && relatedUserId) {
        const { data: existingNotification, error: checkError } = await supabase
          .from('notifications')
          .select('id')
          .eq('user_id', userId)
          .eq('type', type)
          .eq('related_id', relatedId.toString())
          .eq('related_user_id', relatedUserId)
          .maybeSingle();

        if (checkError) {
          console.error('[NotificationService] Error checking existing notification:', checkError);
        } else if (existingNotification) {
          console.log(`[NotificationService] Notification already exists, skipping: ${existingNotification.id}`);
          return true; // Return true since notification already exists
        }
      }

      // Check if related_user_id column exists by trying to insert with it first
      let notificationData = {
        user_id: userId,
        type,
        related_id: relatedId,
        is_read: false
      };

      // Only add related_user_id if it's provided
      if (relatedUserId) {
        notificationData.related_user_id = relatedUserId;
      }

      const { error } = await supabase
        .from('notifications')
        .insert(notificationData);

      if (error) {
        console.error('[NotificationService] Error creating notification:', error);
        throw error;
      }

      console.log(`[NotificationService] Notification created successfully`);
      return true;
    } catch (error) {
      console.error('[NotificationService] Failed to create notification:', error);
      return false;
    }
  }

  /**
   * Create notification when someone follows a user
   * @param {string} followerId - The user who is following
   * @param {string} followeeId - The user being followed
   */
  static async notifyFollow(followerId, followeeId) {
    return this.createNotification(followeeId, 'follow', followerId, followerId);
  }

  /**
   * Create notification when someone likes a post
   * @param {string} likerId - The user who liked the post
   * @param {string} postId - The post that was liked
   * @param {string} postAuthorId - The author of the post
   */
  static async notifyPostLike(likerId, postId, postAuthorId) {
    return this.createNotification(postAuthorId, 'like_post', postId, likerId);
  }

  /**
   * Create notification when someone comments on a post
   * @param {string} commenterId - The user who commented
   * @param {string} postId - The post that was commented on
   * @param {string} postAuthorId - The author of the post
   */
  static async notifyPostComment(commenterId, postId, postAuthorId) {
    return this.createNotification(postAuthorId, 'comment_post', postId, commenterId);
  }

  /**
   * Create notification when someone likes a comment
   * @param {string} likerId - The user who liked the comment
   * @param {string} commentId - The comment that was liked
   * @param {string} commentAuthorId - The author of the comment
   */
  static async notifyCommentLike(likerId, commentId, commentAuthorId) {
    return this.createNotification(commentAuthorId, 'like_comment', commentId, likerId);
  }

  /**
   * Create notification when someone sends a message
   * @param {string} senderId - The user who sent the message
   * @param {string} receiverId - The user who received the message
   * @param {string} messageId - The message ID
   */
  static async notifyMessage(senderId, receiverId, messageId) {
    return this.createNotification(receiverId, 'message', messageId, senderId);
  }

  /**
   * Create notification when someone mentions a user in a post or comment
   * @param {string} mentionedUserId - The user who was mentioned
   * @param {string} mentionerId - The user who mentioned them
   * @param {string} relatedId - The post or comment ID
   * @param {string} type - Either 'mention_post' or 'mention_comment'
   */
  static async notifyMention(mentionedUserId, mentionerId, relatedId, type) {
    return this.createNotification(mentionedUserId, type, relatedId, mentionerId);
  }

  /**
   * Create notification when someone shares a post
   * @param {string} sharerId - The user who shared the post
   * @param {string} postId - The post that was shared
   * @param {string} postAuthorId - The author of the post
   */
  static async notifyPostShare(sharerId, postId, postAuthorId) {
    return this.createNotification(postAuthorId, 'share_post', postId, sharerId);
  }

  /**
   * Create notification when someone completes a protocol
   * @param {string} completerId - The user who completed the protocol
   * @param {string} protocolId - The protocol that was completed
   * @param {string} protocolAuthorId - The author of the protocol
   */
  static async notifyProtocolCompletion(completerId, protocolId, protocolAuthorId) {
    return this.createNotification(protocolAuthorId, 'protocol_completion', protocolId, completerId);
  }

  /**
   * Create notification when someone starts following a protocol
   * @param {string} followerId - The user who started following the protocol
   * @param {string} protocolId - The protocol being followed
   * @param {string} protocolAuthorId - The author of the protocol
   */
  static async notifyProtocolFollow(followerId, protocolId, protocolAuthorId) {
    return this.createNotification(protocolAuthorId, 'protocol_follow', protocolId, followerId);
  }

  /**
   * Create notification when someone logs a biohack
   * @param {string} loggerId - The user who logged the biohack
   * @param {string} biohackId - The biohack that was logged
   * @param {string} biohackAuthorId - The author of the biohack
   */
  static async notifyBiohackLog(loggerId, biohackId, biohackAuthorId) {
    return this.createNotification(biohackAuthorId, 'biohack_log', biohackId, loggerId);
  }

  /**
   * Mark a notification as read
   * @param {string} notificationId - The notification ID to mark as read
   */
  static async markAsRead(notificationId) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) {
        console.error('Error marking notification as read:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return false;
    }
  }

  /**
   * Mark all notifications as read for a user
   * @param {string} userId - The user ID
   */
  static async markAllAsRead(userId) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      return false;
    }
  }

  /**
   * Get unread notification count for a user
   * @param {string} userId - The user ID
   */
  static async getUnreadCount(userId) {
    try {
      // Deduplicate requests
      const cacheKey = getCacheKey('NotificationService.getUnreadCount', userId);
      return await dedupeRequest(cacheKey, async () => {
        const { count, error } = await supabase
          .from('notifications')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_read', false);

        if (error) {
          console.error('Error getting unread count:', error);
          throw error;
        }

        return count || 0;
      });
    } catch (error) {
      console.error('Failed to get unread count:', error);
      return 0;
    }
  }

  /**
   * Delete a notification
   * @param {string} notificationId - The notification ID to delete
   */
  static async deleteNotification(notificationId) {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) {
        console.error('Error deleting notification:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Failed to delete notification:', error);
      return false;
    }
  }

  /**
   * Delete all notifications for a user
   * @param {string} userId - The user ID
   */
  static async deleteAllNotifications(userId) {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId);

      if (error) {
        console.error('Error deleting all notifications:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Failed to delete all notifications:', error);
      return false;
    }
  }
}

export default NotificationService;
